import type {
  AuthResponse,
  Budget,
  Category,
  CreateBudgetData,
  CreateCategoryData,
  CreateTransactionData,
  DashboardSummary,
  PaginatedResponse,
  Transaction,
  TransactionFilters,
  UpdateBudgetData,
  UpdateCategoryData,
  UpdateTransactionData,
  User,
  UserStats,
} from './types';
import {
  normalizeUser,
  normalizeTransaction,
  normalizeCategory,
  normalizeBudget,
  normalizeDashboardSummary,
} from './utils';

const BASE_URL = import.meta.env.VITE_API_URL || '/api';

function getToken(): string | null {
  return localStorage.getItem('budgetboard_token');
}

function setToken(token: string): void {
  localStorage.setItem('budgetboard_token', token);
}

function clearToken(): void {
  localStorage.removeItem('budgetboard_token');
}

async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    clearToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    try {
      const errorBody = await response.json();
      if (errorBody.error) {
        errorMessage = errorBody.error;
      }
    } catch {
      // Use default error message
    }
    throw new Error(errorMessage);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

// Auth API
export const auth = {
  async register(
    email: string,
    password: string,
    name: string
  ): Promise<AuthResponse> {
    const data = await apiFetch<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    });
    setToken(data.token);
    return data;
  },

  async login(email: string, password: string): Promise<AuthResponse> {
    const data = await apiFetch<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setToken(data.token);
    return data;
  },

  async getMe(): Promise<User> {
    const data = await apiFetch<{ user: Record<string, unknown> }>('/auth/me');
    return normalizeUser(data.user);
  },

  logout(): void {
    const token = getToken();
    if (token) {
      apiFetch('/auth/logout', { method: 'POST' }).catch(() => {});
    }
    clearToken();
  },

  async updateProfile(name: string): Promise<User> {
    const data = await apiFetch<{ user: Record<string, unknown> }>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
    return normalizeUser(data.user);
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await apiFetch<{ message: string }>('/auth/password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  async getStats(): Promise<UserStats> {
    return apiFetch<UserStats>('/auth/stats');
  },

  async deleteAccount(): Promise<void> {
    await apiFetch<{ message: string }>('/auth/account', {
      method: 'DELETE',
    });
    clearToken();
  },
};

// Transactions API
export const transactions = {
  async list(
    filters: TransactionFilters = {}
  ): Promise<PaginatedResponse<Transaction>> {
    const params = new URLSearchParams();
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.type) params.set('type', filters.type);
    if (filters.categoryId) params.set('category_id', filters.categoryId);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    if (filters.search) params.set('search', filters.search);
    if (filters.sortBy) params.set('sortBy', filters.sortBy);
    if (filters.sortOrder) params.set('sortOrder', filters.sortOrder);

    const query = params.toString();
    const res = await apiFetch<{
      transactions: Record<string, unknown>[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>(`/transactions${query ? `?${query}` : ''}`);
    return {
      data: (res.transactions || []).map(normalizeTransaction),
      total: res.total,
      page: res.page,
      limit: res.limit,
      totalPages: res.totalPages,
    };
  },

  async getById(id: string): Promise<Transaction> {
    const res = await apiFetch<{ transaction: Record<string, unknown> }>(
      `/transactions/${id}`
    );
    return normalizeTransaction(res.transaction);
  },

  async create(data: CreateTransactionData): Promise<Transaction> {
    const payload = {
      amount: data.amount,
      type: data.type,
      category_id: data.categoryId,
      description: data.description,
      date: data.date,
      is_recurring: data.isRecurring,
      recurrence_interval: data.recurrenceInterval,
    };
    const res = await apiFetch<{ transaction: Record<string, unknown> }>('/transactions', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return normalizeTransaction(res.transaction);
  },

  async update(id: string, data: UpdateTransactionData): Promise<Transaction> {
    const payload: Record<string, unknown> = {};
    if (data.amount !== undefined) payload.amount = data.amount;
    if (data.type !== undefined) payload.type = data.type;
    if (data.categoryId !== undefined) payload.category_id = data.categoryId;
    if (data.description !== undefined) payload.description = data.description;
    if (data.date !== undefined) payload.date = data.date;

    const res = await apiFetch<{ transaction: Record<string, unknown> }>(
      `/transactions/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      }
    );
    return normalizeTransaction(res.transaction);
  },

  async delete(id: string): Promise<void> {
    return apiFetch<void>(`/transactions/${id}`, {
      method: 'DELETE',
    });
  },

  async exportCsv(filters: TransactionFilters = {}): Promise<Blob> {
    const params = new URLSearchParams();
    if (filters.type) params.set('type', filters.type);
    if (filters.categoryId) params.set('category_id', filters.categoryId);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);

    const query = params.toString();
    const token = getToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(
      `${BASE_URL}/transactions/export/csv${query ? `?${query}` : ''}`,
      { headers }
    );

    if (!response.ok) {
      throw new Error('Failed to export transactions');
    }

    return response.blob();
  },
};

// Categories API
export const categories = {
  async list(): Promise<Category[]> {
    const res = await apiFetch<{ categories: Record<string, unknown>[] }>('/categories');
    return (res.categories || []).map(normalizeCategory);
  },

  async create(data: CreateCategoryData): Promise<Category> {
    const res = await apiFetch<{ category: Record<string, unknown> }>('/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return normalizeCategory(res.category);
  },

  async update(id: string, data: UpdateCategoryData): Promise<Category> {
    const res = await apiFetch<{ category: Record<string, unknown> }>(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return normalizeCategory(res.category);
  },

  async delete(id: string): Promise<void> {
    return apiFetch<void>(`/categories/${id}`, {
      method: 'DELETE',
    });
  },
};

// Budgets API
export const budgets = {
  async list(month?: number, year?: number): Promise<Budget[]> {
    const params = new URLSearchParams();
    if (month) params.set('month', String(month));
    if (year) params.set('year', String(year));

    const query = params.toString();
    const res = await apiFetch<{ budgets: Record<string, unknown>[] }>(
      `/budgets${query ? `?${query}` : ''}`
    );
    return (res.budgets || []).map(normalizeBudget);
  },

  async create(data: CreateBudgetData): Promise<Budget> {
    const payload = {
      category_id: data.categoryId,
      amount: data.amount,
      month: data.month,
      year: data.year,
    };
    const res = await apiFetch<{ budget: Record<string, unknown> }>('/budgets', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return normalizeBudget(res.budget);
  },

  async update(id: string, data: UpdateBudgetData): Promise<Budget> {
    const res = await apiFetch<{ budget: Record<string, unknown> }>(`/budgets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return normalizeBudget(res.budget);
  },

  async delete(id: string): Promise<void> {
    return apiFetch<void>(`/budgets/${id}`, {
      method: 'DELETE',
    });
  },
};

// Dashboard API
export const dashboard = {
  async getSummary(): Promise<DashboardSummary> {
    const raw = await apiFetch<Record<string, unknown>>('/dashboard/summary');
    return normalizeDashboardSummary(raw);
  },
};
